import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useCookieConsent } from '@/hooks/use-cookie-consent';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Cookie, Settings } from 'lucide-react';

export const CookieBanner = () => {
  const { consent, loading, saveConsent } = useCookieConsent();
  const [showBanner, setShowBanner] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Animate banner entrance
  useEffect(() => {
    if (!loading && !consent?.consentGiven && showBanner) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [loading, consent, showBanner]);

  // Don't show banner in test environment
  if (typeof window !== 'undefined' && (window as Window & { playwrightTest?: boolean }).playwrightTest) {
    return null;
  }

  // Don't show banner if consent already given or still loading
  if (loading || consent?.consentGiven || !showBanner) {
    return null;
  }

  const handleAcceptAll = async () => {
    await saveConsent({ analytics: true, marketing: true });
    setIsVisible(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  const handleAcceptNecessary = async () => {
    await saveConsent({ analytics: false, marketing: false });
    setIsVisible(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  const handleCustomize = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Navigate to cookies page using window.location
    // CookieBanner is rendered outside Router context, so we use direct navigation
    window.location.href = '/cookies';
  };

  const handleKeyDown = (e: KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div
      ref={bannerRef}
      className={`fixed inset-x-0 bottom-0 z-50 border-t bg-background p-4 shadow-lg transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
      role="dialog"
      aria-label="Cookie consent banner"
      aria-describedby="cookie-banner-description"
    >
      <div className="container mx-auto max-w-6xl">
        <Alert>
          <Cookie className="size-4" />
          <AlertDescription className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <p id="cookie-banner-description" className="text-sm text-foreground">
                We use cookies to enhance your experience. Necessary cookies are always enabled.
                You can choose to enable analytics and marketing cookies to help us improve our services.{' '}
                <button
                  onClick={handleCustomize}
                  onKeyDown={(e) => handleKeyDown(e, handleCustomize)}
                  className="rounded underline hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  aria-label="Learn more about cookies"
                >
                  Learn more
                </button>
              </p>
            </div>
            <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
              <Button
                onClick={handleAcceptNecessary}
                onKeyDown={(e) => handleKeyDown(e, handleAcceptNecessary)}
                variant="outline"
                size="sm"
                className="w-full focus:ring-2 focus:ring-offset-2 sm:w-auto"
                aria-label="Accept only necessary cookies"
              >
                Necessary Only
              </Button>
              <Button
                onClick={handleCustomize}
                onKeyDown={(e) => handleKeyDown(e, handleCustomize)}
                variant="secondary"
                size="sm"
                className="flex w-full items-center gap-2 focus:ring-2 focus:ring-offset-2 sm:w-auto"
                aria-label="Customise cookie preferences"
              >
                <Settings className="size-4" />
                Customise
              </Button>
              <Button
                onClick={handleAcceptAll}
                onKeyDown={(e) => handleKeyDown(e, handleAcceptAll)}
                size="sm"
                className="w-full focus:ring-2 focus:ring-offset-2 sm:w-auto"
                aria-label="Accept all cookies"
              >
                Accept All
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};
